import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  FaBars,
  FaBell,
  FaMoon,
  FaSignOutAlt,
  FaSun,
  FaTimes,
} from 'react-icons/fa';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '../AuthContext';
import { clearAuthenticatedQueryCache } from '../authenticatedQueryCache';
import usePremiumEntitlement from '../hooks/usePremiumEntitlement';
import { clearSimpleBotSetup } from '../simpleBotSetup';
import { currentFrontendMatches, hasActiveCustomerAction, hasInProgressCustomerInput, isLocalNotificationPath } from '../frontendReleaseNavigation';

const authenticatedPrimaryLinks = [
  ['/dashboard', 'Today'],
  ['/portfolio', 'Portfolio'],
  ['/bots', 'Your Bots'],
  ['/settings', 'Advanced'],
  ['/stock-explorer', 'Explore'],
  ['/live-log', 'Activity'],
  ['/help', 'Help'],
  ['/account-settings', 'Account'],
];

const publicLinks = [
  ['/login', 'Login'],
  ['/signup', 'Sign up'],
  ['/help', 'Help'],
  ['/subscribe', 'Plans'],
];

const NavigationBar = () => {
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const [showNotifications, setShowNotifications] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openingNotificationId, setOpeningNotificationId] = useState(null);
  const [notificationNavigationMessage, setNotificationNavigationMessage] = useState(null);
  const notificationsRef = useRef(null);
  const editedFields = useRef(new WeakSet());
  const { token, isTokenVerified, axiosInstance, setToken } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const authenticated = Boolean(token && isTokenVerified);
  const premiumQuery = usePremiumEntitlement(authenticated);
  const adminQuery = useQuery({
    queryKey: ['admin', 'session'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/admin/session');
      return response.data;
    },
    enabled: authenticated,
    retry: false,
    staleTime: 60000,
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const recordEdit = (event) => editedFields.current.add(event.target.closest?.('[contenteditable="true"]') || event.target);
    document.addEventListener('input', recordEdit);
    document.addEventListener('change', recordEdit);
    return () => {
      document.removeEventListener('input', recordEdit);
      document.removeEventListener('change', recordEdit);
    };
  }, []);

  useEffect(() => {
    setShowNotifications(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setMobileMenuOpen(false);
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!showNotifications) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!notificationsRef.current?.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setShowNotifications(false);
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [showNotifications]);

  const { data: attention = { unread_count: 0, needs_attention: [], recent_activity: [] }, isLoading } = useQuery({
    queryKey: ['user-attention'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/notifications');
      return response.data;
    },
    enabled: authenticated,
    retry: 1,
    staleTime: 60000,
    refetchInterval: 60000,
  });
  const refreshAttention = () => queryClient.invalidateQueries({ queryKey: ['user-attention'] });
  const readMutation = useMutation({
    mutationFn: (eventId) => axiosInstance.post(`/api/notifications/${eventId}/read`),
    onSuccess: refreshAttention,
  });
  const archiveMutation = useMutation({
    mutationFn: (eventId) => axiosInstance.post(`/api/notifications/${eventId}/archive`),
    onSuccess: refreshAttention,
  });
  const readAllMutation = useMutation({
    mutationFn: () => axiosInstance.post('/api/notifications/read-all'),
    onSuccess: refreshAttention,
  });
  const needsAttention = attention.needs_attention || [];
  const recentActivity = attention.recent_activity || [];
  const unreadCount = Number(attention.unread_count || 0);

  const deferForCurrentWork = (id, path) => {
    if (queryClient.isMutating() > 0 || hasActiveCustomerAction()) {
      setNotificationNavigationMessage({ id, text: 'An action is still being recorded. Wait for its result, then open this notification again.' });
      return true;
    }
    if (hasInProgressCustomerInput(document, editedFields.current)) {
      setNotificationNavigationMessage({ id, path, text: 'You have work in progress. Finish it first, or choose Open anyway to leave this page without saving it.' });
      return true;
    }
    return false;
  };

  const openAttentionAction = async (item) => {
    if (openingNotificationId) return;
    const path = item.action?.path;
    if (!isLocalNotificationPath(path)) {
      setNotificationNavigationMessage({ id: item.id, text: 'This notification has no valid destination. Please contact support.' });
      return;
    }
    if (deferForCurrentWork(item.id, path)) return;
    setOpeningNotificationId(item.id);
    setNotificationNavigationMessage(null);
    try {
      const sameVersion = await currentFrontendMatches();
      if (deferForCurrentWork(item.id, path)) return;
      if (!sameVersion) {
        window.location.assign(path);
        return;
      }
      try {
        if (!item.canonical_attention && !item.read_at) await readMutation.mutateAsync(item.id);
      } finally {
        setShowNotifications(false);
        navigate(path);
      }
    } finally {
      setOpeningNotificationId(null);
    }
  };

  const continueDeferredNavigation = (message) => {
    if (queryClient.isMutating() > 0 || hasActiveCustomerAction()) {
      setNotificationNavigationMessage({ id: message.id, text: 'An action is still being recorded. Wait for its result, then open this notification again.' });
      return;
    }
    if (!isLocalNotificationPath(message.path)) return;
    window.location.assign(message.path);
  };

  const { data: environmentData } = useQuery({
    queryKey: ['environment'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/status');
      return response.data;
    },
    enabled: authenticated,
    retry: 1,
    staleTime: 60000,
  });

  const environment = environmentData?.mode === 'live' ? 'live' : 'paper';
  const links = authenticated
    ? [
      ...authenticatedPrimaryLinks.slice(0, 2),
      ...(premiumQuery.data?.entitlements?.premium_backtesting === true ? [['/backtests', 'Backtests']] : []),
      ...authenticatedPrimaryLinks.slice(2),
      ...(adminQuery.data?.is_admin === true ? [['/admin', 'Admin']] : []),
      ['/subscribe', 'Plans'],
    ]
    : publicLinks;

  const handleLogout = () => {
    // Session revocation is server-authoritative. Local cleanup is immediate
    // so sign-out still completes if the network is unavailable.
    axiosInstance.post('/api/logout').catch(() => undefined);
    clearSimpleBotSetup();
    clearAuthenticatedQueryCache(queryClient);
    localStorage.removeItem('token');
    setToken(null);
    setMobileMenuOpen(false);
    navigate('/login');
  };

  const navLinkClass = ({ isActive }) => [
    'rounded-lg px-3 py-2 text-sm font-semibold transition',
    isActive
      ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300'
      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white',
  ].join(' ');

  return (
    <nav className={`fixed inset-x-0 top-0 z-50 border-b bg-white/95 shadow-sm backdrop-blur dark:bg-slate-950/95 ${
      environment === 'live' ? 'border-emerald-500' : 'border-blue-500'
    }`}>
      <div className="mx-auto flex h-16 max-w-screen-2xl items-center justify-between px-4 sm:px-6">
        <Link to={authenticated ? '/dashboard' : '/'} className="flex items-center gap-3" aria-label="Pinngro home">
          <img
            src="/pinngro-logo.svg"
            alt="Pinngro"
            className="h-6 w-auto dark:brightness-0 dark:invert"
          />
          {authenticated && (
            <span className={`hidden rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide sm:inline ${
              environment === 'live'
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                : 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300'
            }`}>
              {environment}
            </span>
          )}
        </Link>

        <div className="hidden items-center gap-1 xl:flex">
          {links.map(([path, label]) => (
            <NavLink key={path} to={path} className={navLinkClass}>
              {label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {authenticated && (
            <div ref={notificationsRef} className="relative">
              <button
                type="button"
                onClick={() => setShowNotifications((current) => !current)}
                className="relative rounded-xl bg-slate-100 p-2.5 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                aria-label="Notifications"
                aria-expanded={showNotifications}
                aria-haspopup="dialog"
              >
                <FaBell />
                {unreadCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              {showNotifications && (
                <div
                  className="fixed left-4 right-4 top-[4.5rem] overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-950 shadow-2xl dark:border-slate-800 dark:bg-slate-900 dark:text-white sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-3 sm:w-[min(24rem,calc(100vw-2rem))]"
                  role="dialog"
                  aria-label="Notifications"
                >
                  <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
                    <div>
                      <p className="text-sm font-bold">Notifications</p>
                      <p className="text-xs text-slate-500">
                        {unreadCount === 0 ? 'Nothing unread' : `${unreadCount} unread ${unreadCount === 1 ? 'notification' : 'notifications'}`}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowNotifications(false)}
                      className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
                      aria-label="Close notifications"
                    >
                      <FaTimes />
                    </button>
                  </div>
                  <div className="max-h-[min(28rem,calc(100vh-8rem))] overflow-y-auto p-2">
                    {isLoading ? (
                      <p className="p-3 text-sm">Loading notifications…</p>
                    ) : needsAttention.length === 0 && recentActivity.length === 0 ? (
                      <p className="p-3 text-sm text-slate-500">Nothing needs your attention.</p>
                    ) : (
                      <>
                        {needsAttention.length > 0 && <NotificationGroup title="Needs your attention" items={needsAttention} onOpen={openAttentionAction} onArchive={(id) => archiveMutation.mutate(id)} openingId={openingNotificationId} navigationMessage={notificationNavigationMessage} onContinue={continueDeferredNavigation} />}
                        {recentActivity.length > 0 && <NotificationGroup title="Recent activity" items={recentActivity} onOpen={openAttentionAction} onArchive={(id) => archiveMutation.mutate(id)} openingId={openingNotificationId} navigationMessage={notificationNavigationMessage} onContinue={continueDeferredNavigation} />}
                      </>
                    )}
                    <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-3 pb-2 pt-3 text-xs dark:border-slate-800">
                      <button type="button" onClick={() => readAllMutation.mutate()} disabled={unreadCount === 0 || readAllMutation.isPending} className="font-bold text-blue-700 disabled:text-slate-400 dark:text-blue-300">Mark all read</button>
                      <div className="flex items-center gap-3"><Link to="/account-settings#notifications" onClick={() => setShowNotifications(false)} className="font-bold text-blue-700 dark:text-blue-300">Notification settings</Link><Link to="/live-log" onClick={() => setShowNotifications(false)} className="font-bold text-blue-700 dark:text-blue-300">View all activity</Link></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={() => setTheme((current) => (current === 'light' ? 'dark' : 'light'))}
            className="rounded-xl bg-slate-100 p-2.5 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            aria-label={`Use ${theme === 'light' ? 'dark' : 'light'} theme`}
            title={`Use ${theme === 'light' ? 'dark' : 'light'} theme`}
          >
            {theme === 'light' ? <FaMoon /> : <FaSun />}
          </button>

          {authenticated && (
            <button
              type="button"
              onClick={handleLogout}
              className="hidden items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-red-50 hover:text-red-700 dark:text-slate-300 dark:hover:bg-red-950/40 dark:hover:text-red-300 sm:flex"
            >
              <FaSignOutAlt /> Logout
            </button>
          )}

          <button
            type="button"
            onClick={() => setMobileMenuOpen((current) => !current)}
            className="rounded-xl bg-slate-100 p-2.5 text-slate-700 dark:bg-slate-800 dark:text-slate-200 xl:hidden"
            aria-label="Toggle navigation"
            aria-expanded={mobileMenuOpen}
            aria-controls="primary-mobile-navigation"
          >
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div id="primary-mobile-navigation" className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-slate-200 bg-white px-4 py-4 shadow-lg dark:border-slate-800 dark:bg-slate-950 xl:hidden">
          <div className="mx-auto grid max-w-screen-2xl gap-1 sm:grid-cols-2">
            {links.map(([path, label]) => (
              <NavLink
                key={path}
                to={path}
                onClick={() => setMobileMenuOpen(false)}
                className={navLinkClass}
              >
                {label}
              </NavLink>
            ))}
            {authenticated && (
              <button
                type="button"
                onClick={handleLogout}
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950/40 sm:hidden"
              >
                <FaSignOutAlt /> Logout
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

const NotificationGroup = ({ title, items, onOpen, onArchive, openingId, navigationMessage, onContinue }) => (
  <section aria-label={title} className="mb-2 last:mb-0">
    <h2 className="px-3 pb-1 pt-2 text-xs font-black uppercase tracking-wide text-slate-500">{title}</h2>
    {items.map((item) => (
      <article key={item.id} className={`border-b border-slate-100 p-3 last:border-0 dark:border-slate-800 ${item.read_at ? 'opacity-75' : 'bg-blue-50/70 dark:bg-blue-950/20'}`}>
        <div className="flex items-start gap-2">
          <button type="button" onClick={() => onOpen(item)} disabled={Boolean(openingId)} className="min-w-0 flex-1 text-left disabled:opacity-70">
            <p className="text-sm font-bold text-slate-900 dark:text-white">{item.title}</p>
            <p className="mt-1 text-sm leading-5 text-slate-600 dark:text-slate-300">{item.detail}</p>
            <p className="mt-1 text-xs text-slate-500">{new Date(item.occurred_at).toLocaleString()}{item.action?.label ? ` · ${openingId === item.id ? 'Opening…' : item.action.label}` : ''}</p>
          </button>
          {!item.canonical_attention && <button type="button" aria-label={`Archive ${item.title}`} onClick={() => onArchive(item.id)} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200">×</button>}
        </div>
        {navigationMessage?.id === item.id && <div role="status" className="mt-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">
          <p>{navigationMessage.text}</p>
          {navigationMessage.path && <button type="button" onClick={() => onContinue(navigationMessage)} className="mt-2 min-h-11 rounded-lg border border-amber-700 px-3 font-bold">Open anyway</button>}
        </div>}
      </article>
    ))}
  </section>
);

export default NavigationBar;
